"use client"
import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux" // Added useSelector
import { updateTransaction, fetchInvoices, resetSuccess } from "../../../../redux/features/invoiceSlice" // Updated import path and added fetchInvoices
import ClientInformation from "./ClientInformation"
import PaymentMethodSelector from "./PaymentMethod"
import BankDetails from "./BankDetails"
import TransactionDetails from "./TransactionDetail"
import SuccessModal from "./SuccessModel"
import { fetchApplications } from "../../../../redux/features/applicationSlice"
import CardDetails from "./CardPaymentForm"
// import { Loader2 } from "lucide-react" // Import Loader icon

const TRANSACTION_ID_MIN_LENGTH = 6
const TRANSACTION_ID_MAX_LENGTH = 20

export default function PaymentForm() {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { state } = location
console.log("Received state in PaymentForm:", state)
  const normalizedPaymentInfo = {
    ...(state?.paymentInfo || {}),
    clientName: state?.paymentInfo?.clientName || state?.name || "",
    applicationNo: state?.paymentInfo?.applicationNo || state?.applicationNo || "",
    amount:
      state?.paymentInfo?.amount ??
      state?.amount ??
      "",
  }

  // Get Redux state
  const { isSubmitting, success, error: reduxError, currentPage } = useSelector((state) => state.invoices)

  const [formData, setFormData] = useState({
    applicationId: "",
    transactionDate: "",
    transactionId: "",
    file: null,
    paymentMethod: "bank",
  })

  const [localError, setLocalError] = useState(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)

  useEffect(() => {
    if (!state || !state.bankDetails || !state.paymentInfo) {
      console.log("No payment data found, redirecting back")
      const returnUrl = sessionStorage.getItem("paymentReturnUrl")
      if (returnUrl) {
        navigate(returnUrl)
      } else {
        navigate(-1)
      }
      return
    }

    if (state.returnUrl) {
      sessionStorage.setItem("paymentReturnUrl", state.returnUrl)
    }

    if (state.paymentInfo && state.paymentInfo.applicationNo) {
      setFormData((prev) => ({
        ...prev,
        applicationId: state.applicationId,
        transactionDate: state.paymentInfo.transactionDate || prev.transactionDate,
        transactionId: state.paymentInfo.transactionId || prev.transactionId,
      }))
    }
  }, [state, navigate])

  // Monitor success state from Redux
  useEffect(() => {
    if (success) {
      setShowSuccessModal(true)
      // Reset form data on success
      setFormData((prev) => ({
        ...prev,
        transactionDate: "",
        transactionId: "",
        file: null,
      }))
    }
  }, [success])

  if (!state) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="w-8 h-8 border-4 border-blue-900 border border-b-white rounded-full animate-spin"></div>
      </div>
    );
  }


  const handleSubmit = async (e) => {
    e.preventDefault()

    const normalizedTransactionId = `${formData.transactionId || ""}`.replace(/[^a-zA-Z0-9-]/g, "")

    // Validate form data
    if (!formData.applicationId || !normalizedTransactionId || !formData.transactionDate || !formData.file) {
      setLocalError("Please fill all required fields and upload proof of payment")
      return
    }

    if (
      normalizedTransactionId.length < TRANSACTION_ID_MIN_LENGTH ||
      normalizedTransactionId.length > TRANSACTION_ID_MAX_LENGTH
    ) {
      setLocalError(
        `Transaction ID must be ${TRANSACTION_ID_MIN_LENGTH}-${TRANSACTION_ID_MAX_LENGTH} characters.`,
      )
      return
    }

    setLocalError(null)

    try {
      // Dispatch the updateTransaction action
      const resultAction = await dispatch(
        updateTransaction({
          applicationId: formData.applicationId,
          transactionId: normalizedTransactionId,
          transactionDate: formData.transactionDate,
          paymentMethod: formData.paymentMethod,
          file: formData.file,
        }),
      )

      // Check if the action was fulfilled
        if (updateTransaction.fulfilled.match(resultAction)) {
        // Fetch invoices again to update the list (preserve current page)
        dispatch(fetchInvoices({ page: currentPage, limit: 10 }))
        dispatch(fetchApplications())

      }
    } catch (err) {
      setLocalError("An unexpected error occurred")
      console.error(err)
    }
  }

  const handleModalClose = () => {
    setShowSuccessModal(false)
    dispatch(resetSuccess())
    const returnUrl = state?.returnUrl || sessionStorage.getItem("paymentReturnUrl")
    if (returnUrl) {
      sessionStorage.removeItem("paymentReturnUrl")
      navigate(returnUrl)
    } else {
      navigate(`/client/applications/form/${formData.applicationId}`)
    }
  }

  const handleFileChange = (file) => {
    setFormData((prev) => ({ ...prev, file }))
  }

  const handleInputChange = (name, value) => {
    if (name === "transactionId") {
      const normalizedTransactionId = `${value || ""}`
        .replace(/[^a-zA-Z0-9-]/g, "")
        .slice(0, TRANSACTION_ID_MAX_LENGTH)

      setFormData((prev) => ({ ...prev, [name]: normalizedTransactionId }))
      return
    }

    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  // Display either local validation error or Redux error
  const displayError = localError || reduxError

  return (
    <div className="flex items-center justify-center pt-2">
      <div className="bg-[#FFFBF5] border border-[#CAC3BB] rounded-lg shadow-custom p-12 w-full max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <h2 onClick={() => console.log(state.countryOfInterest)} className="text-lg font-medium text-gray-900 text-center">Payment Method</h2>

          

          <ClientInformation paymentInfo={normalizedPaymentInfo} />

          <PaymentMethodSelector
            paymentMethod={formData.paymentMethod}
            onChange={(value) => handleInputChange("paymentMethod", value)}
            countryOfInterest={state.countryOfInterest}
          />

          {formData.paymentMethod === "bank" && <BankDetails bankDetails={state.bankDetails[0]} countryOfInterest={state.countryOfInterest} />}
          {formData.paymentMethod === "creditCard" && <CardDetails paymentInfo={normalizedPaymentInfo} applicationId={formData.applicationId}/>}
          {(state.countryOfInterest === "Australia" && formData.paymentMethod === "payId") && (<div>
            <label className="block text-sm mb-1">Pay Id</label>
            <input type="text" value={state?.bankDetails[0]?.payId} className="w-full p-2 border rounded-md" readOnly />
            {/* show no pay id available text */}
            {state?.bankDetails[0]?.payId ? null : <p className="text-red-500 text-sm mt-2">No Pay Id available for this application.</p>}
          </div>)}

          {(formData.paymentMethod === "bank" || formData.paymentMethod === "payId") && <TransactionDetails
            transactionDate={formData.transactionDate}
            transactionId={formData.transactionId}
            file={formData.file}
            onDateChange={(value) => handleInputChange("transactionDate", value)}
            onIdChange={(value) => handleInputChange("transactionId", value)}
            onFileChange={handleFileChange}
            transactionIdMaxLength={TRANSACTION_ID_MAX_LENGTH}
          />}

          {displayError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{displayError}</div>
          )}

          {formData.paymentMethod !== "creditCard" && <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`bg-[#036494] rounded-full text-white py-2 px-6 hover:bg-[#075985] transition-colors flex items-center justify-center ${isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                }`}
            >
              
              {isSubmitting ? (
                <>
                  <span className="loader-small h-4 w-4 mr-4" />
                  Processing...
                </>
              ) : (
                "Confirm Payment"
              )}
            </button>
          </div>}
        </form>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleModalClose}
        autoRedirectDelay={5000}
        message={success || "Payment  confirmed successfully!"}
      />
    </div>
  )
}